import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { t } from './i18n'
import { zoomConfirmEnabled, zoomTooEarly, OTHER_REASON, CONNECTION_REASON, STUDENT_FIX_DWELL_MS } from './fallbackGate'
import { useFocusTrap } from './useFocusTrap'

/**
 * "Move this class to Zoom" — the confirmation in front of the last resort.
 *
 * Everything about this dialog is trying to slow the tutor down by exactly one
 * beat and no more. It is not a scare screen: by the time someone opens it the
 * lesson is already failing and a paying child is waiting. But the action ends
 * the recording, disconnects every student, and spends a pooled Zoom room, so it
 * asks for a reason first — which is also the only record the quality team ever
 * gets of what keeps breaking.
 *
 * The reason VALUE sent to the server is always the English string, whatever
 * language the tutor is reading. SessionSwitchTicket.categorize_reason buckets
 * these by regex, so a localised value would land every Arabic-speaking tutor's
 * report in "other".
 */

interface ReasonOption {
  /** Sent to the server. English on purpose — see above. */
  value: string
  /** i18n key for what the tutor reads. */
  labelKey:
    | 'fallback.reasonAudio'
    | 'fallback.reasonCamera'
    | 'fallback.reasonShare'
    | 'fallback.reasonConnection'
    | 'fallback.reasonOther'
}

const REASONS: ReasonOption[] = [
  { value: 'Audio problem - nobody can hear', labelKey: 'fallback.reasonAudio' },
  { value: 'Camera or video problem', labelKey: 'fallback.reasonCamera' },
  { value: 'Screen share is not working', labelKey: 'fallback.reasonShare' },
  { value: CONNECTION_REASON, labelKey: 'fallback.reasonConnection' },
  { value: OTHER_REASON, labelKey: 'fallback.reasonOther' },
]

export function FallbackDialog({
  busy,
  error,
  onConfirm,
  onCancel,
  onFix,
  recovering,
  recoveryAttempts,
  recoveryCompleted,
  canAskStudent,
  askSentAt,
  studentAnswered,
  sessionStartAt,
  studentDeviceReason,
  measuredHint,
}: {
  busy: boolean
  error: string | null
  onConfirm: (reason: string) => void
  onCancel: () => void
  /**
   * Fix-before-Zoom. Local recover for a tutor fault; ask-the-student for a
   * student-device fault. Always shown — hiding it left Zoom as the only action.
   */
  onFix: () => void
  /** A recovery is in flight (recovery.running) — the Fix button says so. */
  recovering: boolean
  /**
   * Completed local recoveries, separate from student help requests.
   * See fallbackGate.ts for the outcome and dwell requirements.
   */
  recoveryAttempts: number
  recoveryCompleted?: boolean
  canAskStudent?: boolean
  askSentAt?: number | null
  studentAnswered?: boolean
  sessionStartAt: string
  /**
   * Set when we can SEE that a student's own device is the problem — a camera
   * that does not exist, a microphone the browser is withholding.
   *
   * This dialog used to be a single escape hatch: five reasons, one button,
   * switch. On 2026-08-02 two of fourteen real classes were moved to Zoom for
   * faults Zoom cannot fix (a missing camera; a silent microphone), because after
   * twenty minutes of a child not being heard it was the only button that looked
   * like action. So when the evidence points at the student's hardware, the dialog
   * says so plainly and puts staying FIRST. Zoom is never removed — a tutor who
   * still wants it is one tap away, and trapping them here would only push them
   * to a personal Zoom link we cannot record or safeguard.
   */
  studentDeviceReason?: string | null
  /** Concrete evidence from the last local recovery, already translated. */
  measuredHint?: string
}) {
  const [reason, setReason] = useState<string | null>(null)
  const [otherDetail, setOtherDetail] = useState('')
  const cancelRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  useFocusTrap(modalRef)
  const cancelHandler = useRef(onCancel)
  cancelHandler.current = onCancel

  // Kept current so the Escape handler can read live `busy` without the effect
  // resubscribing on every render.
  const busyRef = useRef(busy)
  busyRef.current = busy

  useEffect(() => {
    cancelRef.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      // Escape must not dismiss a switch already in flight. The parent guards
      // this too; owning it here as well means the dialog stays correct if it is
      // ever reused behind a different onCancel.
      if (event.key === 'Escape' && !busyRef.current) cancelHandler.current()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const [now, setNow] = useState(Date.now)
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])
  const studentPending = askSentAt != null && !studentAnswered && now - askSentAt < STUDENT_FIX_DWELL_MS
  const tooEarly = zoomTooEarly(sessionStartAt, now)
  const confirmEnabled = zoomConfirmEnabled({
    studentDeviceReason,
    reason,
    recoveryAttempts,
    recoveryCompleted,
    canAskStudent,
    askSentAt,
    studentAnswered,
    sessionStartAt,
    now,
  })
  const otherDetailValid = reason !== OTHER_REASON || otherDetail.trim().length >= 10
  const showFix = true

  // Portalled for the same reason ConfirmDialog is: the control bar has a
  // backdrop-filter, which makes it the containing block for fixed-position
  // descendants and would strand this dialog below the fold.
  return createPortal(
    <div
      className="modal-scrim"
      onClick={(event) => {
        // A stray tap outside must not cancel mid-request — the class may
        // already be moving.
        if (event.target === event.currentTarget && !busy) onCancel()
      }}
    >
      <div ref={modalRef} className="modal" role="alertdialog" aria-modal="true" aria-labelledby="fallback-title">
        <div className="modal__icon" aria-hidden="true">
          🛟
        </div>
        <h2 className="modal__title" id="fallback-title">
          {t('fallback.title')}
        </h2>
        <p className="modal__body">{t('fallback.body')}</p>
        {studentDeviceReason ? (
          <p className="modal__body">{studentDeviceReason} {t('fallback.zoomWontFix')}</p>
        ) : null}
        {studentPending ? <p className="modal__body" role="status">{t('fallback.waitForStudent')}</p> : null}
        {measuredHint ? <p className="modal__body">{measuredHint}</p> : null}
        {tooEarly ? <p className="modal__body">{t('fallback.tooEarly')}</p> : null}

        <fieldset className="modal__choices" disabled={busy}>
          <legend>{t('fallback.reasonLegend')}</legend>
          {REASONS.map((option) => (
            <label key={option.value} className="modal__choice">
              <input
                type="radio"
                name="fallback-reason"
                value={option.value}
                checked={reason === option.value}
                onChange={() => setReason(option.value)}
              />
              <span>{t(option.labelKey)}</span>
            </label>
          ))}
        </fieldset>

        {reason === OTHER_REASON ? (
          <label className="modal__choice">
            <span>{t('fallback.otherDetailLabel')}</span>
            <textarea
              required
              minLength={10}
              maxLength={400}
              value={otherDetail}
              onChange={(event) => setOtherDetail(event.target.value)}
              placeholder={t('fallback.otherDetailHint')}
            />
          </label>
        ) : null}

        {error ? (
          <p className="modal__error" role="alert">
            {error}
          </p>
        ) : null}

        {/* Ordering carries the recommendation. With student-hardware evidence,
            staying is the primary action and the label says what staying MEANS,
            because "Stay here" alone reads as "do nothing" to a tutor who has
            already spent fifteen minutes doing nothing. */}
        <div className={`modal__actions ${studentDeviceReason ? 'modal__actions--stay-first' : ''}`}>
          <button
            type="button"
            className={studentDeviceReason ? 'primary-button' : 'pill-button'}
            onClick={onCancel}
            ref={cancelRef}
            disabled={busy}
          >
            {studentDeviceReason ? t('fallback.keepTeaching') : t('fallback.cancel')}
          </button>
          {/* Fix before Zoom. The primary action for a recoverable media fault:
              one tap runs the same recovery the automatic triggers use, and Zoom
              stays locked until it has been tried at least once (see below). */}
          {showFix ? (
            <button
              type="button"
              className="primary-button"
              disabled={busy || recovering || studentPending}
              onClick={() => {
                if (busy || recovering || studentPending) return
                onFix()
              }}
            >
              {recovering
                ? t('fallback.fixing')
                : studentDeviceReason && canAskStudent
                  ? t('fallback.askStudent')
                  : t('fallback.fixNow')}
            </button>
          ) : null}
          <button
            type="button"
            className="pill-button pill-button--danger"
            // Disabled until a reason is picked AND (for a media fault) a recovery
            // has been tried this session — the server refuses a reasonless switch
            // anyway, and Zoom cannot fix a fault nobody has attempted to recover.
            // See fallbackGate.zoomConfirmEnabled for the gate.
            disabled={busy || !confirmEnabled || !otherDetailValid}
            onClick={() => {
              if (busy || reason === null || !confirmEnabled || !otherDetailValid) return
              onConfirm(reason === OTHER_REASON ? `${OTHER_REASON}: ${otherDetail.trim()}` : reason)
            }}
          >
            {busy ? t('fallback.moving') : t('fallback.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
